type Options = Partial<TabsOptions>;

export interface TabsElement extends HTMLElement {
  tabs?: ProductTabs;
}

export interface TabsOptions {
  anchorSelector: string;
  itemSelector: string;
  listSelector: string;
  visibilityClassName: string;
  firstItemOpen: boolean;
}

export class ProductTabs {
  static defaults = {
    anchorSelector: '[data-tabs-anchor]',
    firstItemOpen: false,
    itemSelector: '[data-tabs-item]',
    listSelector: '[data-tabs-list]',
    visibilityClassName: 'is-active',
  };
  options: TabsOptions;
  element: TabsElement;
  readMoreOpen = false;
  descriptionHeightChecked = false;

  constructor(element: TabsElement, options: Options) {
    if (!element || element.tabs) {
      return;
    }

    element.tabs = this;
    this.element = element;
    this.options = {
      ...ProductTabs.defaults,
      ...this.element.dataset,
      ...options,
    };

    this.binding();
  }

  destroy() {
    this.element.tabs = undefined;
  }

  trimAttributeName(attributeName: string) {
    return attributeName.replace('[', '').replace(']', '');
  }

  toggleVisibility(element: Element, items: Element[]) {
    if (element.classList.contains(this.options.visibilityClassName)) {
      element.classList.remove(this.options.visibilityClassName);
    } else {
      this.closeOpen(items);
      element.classList.add(this.options.visibilityClassName);
    }
  }

  closeOpen(items: Element[]) {
    for (const child of items) {
      child.classList.remove(this.options.visibilityClassName);
    }
  }

  setupReadMore() {
    const readMoreWrapper = document.getElementById('description-tab');
    const descriptionAnchor = document.getElementById('description-anchor');
    const config = {attributes: true};

    if (!readMoreWrapper || !descriptionAnchor) {
      return;
    }

    const callback = (mutationsList: MutationRecord[]) => {
      for (const mutation of mutationsList) {
        if (mutation.type === 'attributes') {
          const target = mutation.target as HTMLElement;
          if (
            target.classList.contains(this.options.visibilityClassName) &&
            !this.descriptionHeightChecked
          ) {
            const checkHeight = () => {
              if (readMoreWrapper.offsetHeight >= 115) {
                readMoreWrapper.classList.add('read-more');
              } else {
                readMoreWrapper.classList.remove('read-more');
              }

              this.descriptionHeightChecked = true;
            };

            setTimeout(checkHeight, 300);
          }
        }
      }
    };

    const observer = new MutationObserver(callback);
    observer.observe(descriptionAnchor, config);
  }

  descriptionReadMore(event: Event) {
    event.stopPropagation();

    const descriptionTab = document.getElementById('description-tab');
    const readMoreButton = document.getElementById('read-more-button');

    if (!descriptionTab || !readMoreButton) {
      return;
    }

    if (!this.readMoreOpen) {
      this.readMoreOpen = true;
      readMoreButton.innerText = 'Read Less';
      descriptionTab.classList.add('show-more');
    } else {
      this.readMoreOpen = false;
      readMoreButton.innerText = 'Read More';
      descriptionTab.classList.remove('show-more');
    }
  }

  binding() {
    const items = Array.from(this.element.children);

    for (const child of items) {
      if (
        child.attributes.getNamedItem(
          this.trimAttributeName(this.options.anchorSelector),
        )
      ) {
        const tabLink = child.getElementsByClassName('tab-link')[0];

        tabLink.addEventListener('click', () =>
          this.toggleVisibility(child, items),
        );
      }
    }

    this.setupReadMore();
    const description = document.getElementById('read-more-button');

    if (!description) {
      return;
    }

    description.addEventListener('click', () =>
      this.descriptionReadMore(event!),
    );

    if (this.options.firstItemOpen) {
      this.toggleVisibility(items[0], items);
    }
  }
}
